<template>
  <div v-bind:key="refreshKey">

    <!-- Top pagination -->
    <div class="mb-1 text-center">
      <div class="d-inline-block">
        <b-pagination
            v-model="pagination.page"
            :total-rows="pagination.total"
            :per-page="pagination.size"
            @page-click="onPageClick"
            align="center"
        ></b-pagination>
      </div>

      <div class="d-inline-block">
        <b-select class="d-inline-block" style="width: 70px" size="sm"
                  v-model="pagination.size" @change="refresh">
          <b-select-option :value="20">20</b-select-option>
          <b-select-option :value="25">25</b-select-option>
          <b-select-option :value="50">50</b-select-option>
          <b-select-option :value="100">100</b-select-option>
        </b-select>
        /{{ pagination.total }} {{ $t('columns.counterNames.affiliates') }}
      </div>
    </div>

    <b-table
        responsive
        v-handy-scroll
        :items="affiliatesData"
        :fields="fields"
        :busy="loading"
        striped
        no-footer-sorting
        foot-clone
        foot-row-variant="success"
        style="white-space: nowrap; padding-bottom: 30px"
        :sort-compare="sortItemCompare"
    >

      <!-- Company — rows with no affiliate (e.g. manually duplicated clients) -->
      <template #cell(company)="data">
        <span v-if="data.value">{{ data.value }}</span>
        <b-badge v-else variant="light-warning" class="font-weight-bold">
          {{ $t('columns.notAssigned') }}
        </b-badge>
      </template>

      <!-- Company -->
      <template #cell(company)="data">
        <b-link @click="$emit('selected',data.item.affiliateId)">{{data.value}}</b-link>
      </template>

      <!-- Countries -->
      <template #cell(countries)="data">
        <div v-if="data.value && Object.keys(data.value).length > 0">
          <p v-for="(count, country) in data.value" :key="country" class="font-weight-bold m-0">
            {{ countries[country] }}: {{ count }}
          </p>
        </div>
        <div v-else>
          <p>____</p>
        </div>
      </template>

      <!-- FTDS -->
      <template #cell(ftds)="data">
        <p class="m-0">
          {{ data.value }}
          <span v-if="data.item.totalFtdsInCreated != data.value">
            ({{data.item.totalFtdsInCreated}})
          </span>
        </p>
      </template>



      <!-- FTD AMOUNT -->
      <template #cell(ftdAmount)="data">
        <span :style="{ color: data.value > 0 ? '#4ebd52' : '#c53838' }">
          {{ convertToEURO(data.value) }}</span>
      </template>


      <!-- Net Deposits -->
      <template #cell(netDeposits)="data">
        <span :style="{ color: data.value > 0 ? '#4ebd52' : '#c53838' }">
          {{ convertToEURO(data.value) }}
        </span>
      </template>

      <!-- Conversion Rate -->
      <template #cell(conversionRate)="data" >
        <span>{{ ((data.item.ftds / data.item.leads) * 100).toFixed(2) }}%</span>
      </template>

      <!-- FOOTER -->

      <template #foot(company)>
        <span style="font-size: 15px;font-weight: bold">{{ $t('columns.total') }}</span>
      </template>

      <template #foot(leads)>
        <span style="font-size: 15px;font-weight: bold">{{ totalLeads }}</span>
      </template>

      <template #foot(ftds)>
        <span style="font-size: 15px;font-weight: bold">{{ totalFtds }}</span>
      </template>

      <template #foot(conversionRate)>
        <span style="font-size: 15px;font-weight: bold">
          {{ ((totalFtds / totalLeads) * 100).toFixed(2) }}%
        </span>
      </template>

      <!-- FOOTER: FTD AMOUNT -->
      <template #foot(ftdAmount)>
        <b-badge variant="success" style="font-size:15px;font-weight:bold;">
          {{ convertToEURO(totalFtdAmount) }}
        </b-badge>
      </template>

      <!-- FOOTER: STD AMOUNT -->
      <template #foot(stdAmount)>
        <b-badge variant="primary" style="font-size:15px;font-weight:bold;">
          {{ convertToEURO(totalStdAmount) }}
        </b-badge>
      </template>

      <!-- FOOTER: NET DEPOSITS -->
      <template #foot(netDeposits)>
        <b-badge :variant="totalNetDeposits > 0 ? 'success' : 'danger'"
                 :style="{fontSize:'15px',fontWeight:'bold'}">
          {{ convertToEURO(totalNetDeposits) }}
        </b-badge>
      </template>

      <!-- FOOTER: Countries -->
      <template #foot(countries)>
        <div>
          <b-button id="show-popover" variant="primary" size="sm" @click="togglePopover">
            Show
          </b-button>

          <b-popover :show.sync="showPopover"
                     target="show-popover"
                     placement="top"
                     title="Country Totals"
                     triggers="manual">
            <div v-if="totalByCountry && Object.keys(totalByCountry).length > 0">
              <p v-for="(count, country) in totalByCountry"
                 :key="country" class="font-weight-bold">
                {{ countries[country] }}: {{ count }}
              </p>
            </div>
            <div v-else>
              <p>0</p>
            </div>
          </b-popover>
        </div>
      </template>

    </b-table>

    <!-- Pagination -->
    <div class="mt-2 text-center">
      <div class="d-inline-block">
        <b-pagination
            v-model="pagination.page"
            :total-rows="pagination.total"
            :per-page="pagination.size"
            @page-click="onPageClick"
            align="center"
        ></b-pagination>
      </div>

      <div class="d-inline-block">
        <b-select class="d-inline-block" style="width: 70px" size="sm"
                  v-model="pagination.size" @change="refresh">
          <b-select-option :value="20">20</b-select-option>
          <b-select-option :value="25">25</b-select-option>
          <b-select-option :value="50">50</b-select-option>
          <b-select-option :value="100">100</b-select-option>
        </b-select>
        /{{ pagination.total }} {{ $t('columns.counterNames.affiliates') }}
      </div>

    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import mixinTable from '@/components/mixin/mixinTable';
import mixinNotifications from '@/components/mixin/mixinNotifications';
import mixinBase from '@/components/mixin/mixinBase';

export default {
  name: 'AffiliateDashboardTable',
  mixins: [mixinBase, mixinTable, mixinNotifications],

  data: () => ({
    affiliatesData: [],
    totalNetDeposits: 0.0,
    totalFtdAmount: 0.0,
    totalStdAmount: 0.0,
    totalLeads: 0,
    totalFtds: 0,
    totalByCountry: {},
    showPopover: false,
  }),

  props: {
    filters: { default: () => ({}) },
    columns: { default: () => ([]) },
    lazy: { default: () => false }
  },

  watch: {
    filters: {
      deep: true,
      handler() {
        this.pagination.page = 1;
        this.refresh();
      }
    }
  },

  created() {
    this.setPagination(1, 0, 20);
    this.refresh();
  },

  computed: {
    ...mapGetters('data', ['getBusinessUnitNameById', 'getOrganizationNameById']),

    countries() {
      const countryList = this.$t('countryList');
      return countryList.reduce((acc, c) => {
        acc[c.code] = c.name;
        return acc;
      }, {});
    },

    fields() {
      const $this = this;

      let fields = (this.columns || []).map(column => ({
        key: column.key,
        label: column.key === 'clientId'
            ? $this.$t('columns.client')
            : $this.$t(`columns.${column.key}`),
        sortable: true, // all rows are loaded client-side, so local sort works at any page size
        active: column.enable,
      }));

      fields = fields.filter(f => !f.key.startsWith('id') && f.key !== 'statuses');
      return fields.filter(f => f.active === true);
    }
  },

  methods: {
    ...mapActions('dashboard', ['getAffiliateDashboard']),

    togglePopover() {
      this.showPopover = !this.showPopover;

      if (this.showPopover) {
        document.addEventListener('click', this.handleOutsideClick);
      } else {
        document.removeEventListener('click', this.handleOutsideClick);
      }
    },

    handleOutsideClick(event) {
      const popoverElement = document.getElementById('show-popover');
      if (popoverElement && !popoverElement.contains(event.target)) {
        this.showPopover = false;
        document.removeEventListener('click', this.handleOutsideClick);
      }
    },

    async refresh() {
      const loadingToken = this.setLoading();
      try {
        const result = await this.getAffiliateDashboard({
          pagination: this.pagination,
          filter: this.filters,
        });

        this.affiliatesData = result.data || [];

        // Totals
        this.totalNetDeposits = this.affiliatesData.reduce((t, i) => t + i.netDeposits, 0);
        this.totalFtdAmount = this.affiliatesData.reduce((t, i) => t + i.ftdAmount, 0);
        this.totalStdAmount = this.affiliatesData.reduce((t, i) => t + i.stdAmount, 0);
        this.totalLeads = this.affiliatesData.reduce((t, i) => t + i.leads, 0);
        this.totalFtds = this.affiliatesData.reduce((t, i) => t + i.ftds, 0);
        this.totalByCountry = this.combineCountryTotals(this.affiliatesData);

        this.setMetaDataFromResult(result);
        this.$emit('calculate', this.affiliatesData);

      } catch (err) {
        this.showErrorNotification(
            this.$t('globalMessages.fetchDataError', { system: this.$t('menu.affiliates') }),
            this.getErrorDescription(err)
        );
      } finally {
        this.setLoaded(loadingToken);
      }
    },

    combineCountryTotals(items) {
      const combined = {};
      items.forEach(item => {
        Object.entries(item.countries).forEach(([country, count]) => {
          combined[country] = (combined[country] || 0) + count;
        });
      });
      return combined;
    },

    sortItemCompare(row1, row2, field) {
      try {
        let value1 = row1[field]
        let value2 = row2[field]
        if (field === 'conversionRate') {
          value1 = (row1.ftds / row1.leads)
          value2 = (row2.ftds / row2.leads)
        }
        if (field === 'countries') {
          value1 = Object.values(row1.countries || {}).reduce((a, b) => a + b, 0)
          value2 = Object.values(row2.countries || {}).reduce((a, b) =>a + b, 0)
        }
        if (value1 == null) value1 = ''
        if (value2 == null) value2 = ''
        if (typeof value1 === 'number' && typeof value2 === 'number') {
          return value1 - value2
        }
        if(value1.localeCompare === undefined || value2.localeCompare === undefined){
          return 0
        }
        return value1.localeCompare(value2)
      } catch (e) {
        console.warn('error in sortItemCompare', e)
        return 0
      }
    },
  }
};
</script>

<style scoped>
.custom-modal { min-width: 500px; }
.btn-container .btn { margin-right: 2px; }
.color-badge { display: inline-block; width: 15rem; height: 1rem; }
</style>
